import { FC, useState } from "react";
import Card from "components/common/Card";
import { TickIcon } from "components/common/Icons";
import cross from "assets/icons/close.svg";
import tooltipIcon from "assets/icons/tooltip.svg";
import { Tooltip } from "react-tooltip";
import DOMPurify from "dompurify";
import arrowIcon from "assets/icons/arrow-button.svg";

const TableModal = ({ tableData, onClose }: { tableData: string; onClose: () => void }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
      <div className="relative bg-white p-6 rounded-lg min-w-[200px] max-w-[80vw] max-h-[80vh] overflow-auto gap-5">
        <button className="absolute top-3 right-3 text-gray-500 hover:text-gray-800" onClick={onClose}>
          <img src={cross} alt="Close" className="w-3 h-3" />
        </button>
        <div className="p-2" dangerouslySetInnerHTML={{ __html: tableData }} />
      </div>
    </div>
  );
};

interface BenefitCardProps {
  id: string;
  cardData: any;
  addOns?: any;
}

const screenData: any = {
  coverageAndLimits: { name: "Coverage And Limits", borderColor: "border-[#CFE0EC]", bgColor: "bg-[#E5F8FF]", textColor: "text-[#0B7A9F]", tickColor: "#0B7A9F", bg: "#E5F8FF" },
  otherBenefitsAndLimits: { name: "Other Benefits & Limits", borderColor: "border-[#D5E7C8]", bgColor: "bg-[#F3FFEA]", textColor: "text-[#78995D]", tickColor: "#78995D", bg: "#F3FFEA" },
  waitingPeriodAndSubLimitsV2: { name: "Waiting Periods", borderColor: "border-[#E3E5C3]", bgColor: "bg-[#FEFFE8]", textColor: "text-[#9EA147]", tickColor: "#9EA147", bg: "#FEFFE8" },
  exclusions: { name: "Exclusions", borderColor: "border-[#E7CBCA]", bgColor: "bg-[#FFEFEE]", textColor: "text-[#C67C78]", tickColor: "#C67C78" }
};

const RecommendationbenefitCard: FC<BenefitCardProps> = ({ id, cardData}) => {
  const [showTableModal, setShowTableModal] = useState<{ show: boolean; key: string }>({ show: false, key: "" });

  const htmlHandler = (html: string, bg: string): string => {
    const sanitizedHtml = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["b", "i", "em", "strong", "a", "span", "div", "p", "button", "li", "ul"],
      ALLOWED_ATTR: ["href", "style"]
    });

    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizedHtml, "text/html");

    const buttons = doc.querySelectorAll("button");

    buttons.forEach((button) => {
      let text = button.textContent?.trim() || "";

      // Remove everything after colon
      text = text.split(":")[0].trim();

      const styledSpan = doc.createElement("span");
      styledSpan.setAttribute(
        "style",
        `
      background-color: ${bg};
      display: inline-flex;
      align-items: center;
      gap: 4px;
      cursor: pointer;
      font-size: 13px;
      padding-inline: 10px;
      border: 1px solid #DEDFB7;
      border-radius: 4px;
    `
      );
      styledSpan.innerHTML = `
      ${text}
      <img src="${arrowIcon}" style="width:14px; height:14px;" />
    `;

      button.replaceWith(styledSpan);
    });

    return doc.body.innerHTML;
  };

  return (
    <>
      {/* Web View - Only visible on desktop */}
      <Card parentClassName="my-10 relative w-full hidden md:flex" className="w-full">
        <p className={`${screenData[id].borderColor} ${screenData[id].textColor} font-extrabold absolute right-0 -top-6 text-sm bg-white border-[3px] p-1 px-3 rounded-[4px]`}>{screenData[id].name}</p>

        <div className={`absolute -top-5 -bottom-5 -left-3 ${id === "exclusions" ? "w-[8%]" : "w-[27%]"} border-[5px] rounded-[15px] ${screenData[id].borderColor} ${screenData[id].bgColor}`}></div>

        {/* Render content for benefits cards */}
        {["otherBenefitsAndLimits", "waitingPeriodAndSubLimitsV2", "coverageAndLimits"].includes(id) &&
          Object.values(cardData || {})
            .filter((obj: any) => obj?.key)
            .map((obj: any, i: number, arr: any[]) => {
              const isTableData = Boolean(obj?.table_data);
              return (
                <div key={i} className={`relative items-center w-full ${i !== arr.length - 1 ? "border-b-2 border-[#0000001A]" : ""} ${obj?.key ? "flex" : "hidden"} -ml-2`}>
                  <div className="flex gap-3 text-sm md:text-base font-extrabold items-center p-5 w-[27%]">
                    <p className="w-4">
                      <TickIcon color={screenData[id].tickColor} />
                    </p>
                    <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(obj?.key) }} />
                    {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}{" "}
                    <Tooltip
                      delayShow={1}
                      key={i}
                      id={obj?.tooltip}
                      place="right"
                      className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg"
                      render={() => (
                        <div className="text-white text-sm font-medium ">{obj?.tooltip}</div> // Ensure normal font weight
                      )}
                    />
                  </div>
                  <div className={`w-[70%] p-5`}>
                    <div className="flex flex-wrap gap-1">
                      <p className="font-semibold text-gray-800 flex flex-wrap gap-1" dangerouslySetInnerHTML={{ __html: htmlHandler(obj?.value, screenData[id].bg) }} onClick={() => (isTableData ? setShowTableModal({ show: true, key: obj?.table_data }) : null)} />
                    </div>
                    {showTableModal.show && <TableModal tableData={showTableModal.key} onClose={() => setShowTableModal({ show: false, key: "" })} />}
                  </div>
                </div>
              );
            })}

        {/* Render content for exclusions */}
        {id === "exclusions" &&
          cardData &&
          cardData.map((data: any, i: number) => (
            <div key={i} className={`relative flex justify-center items-center w-full ${i !== cardData.length - 1 ? "border-b-2 border-[#0000001A]" : ""} -ml-2`}>
              <div className="flex items-center justify-center h-full w-[8%] -ml-2">
                <img src={cross} className="object-contain" alt="Excluded" />
              </div>

              <div className="w-[92%] p-5">
                <p className="font-semibold text-gray-800" dangerouslySetInnerHTML={{ __html: data }}></p>
              </div>
            </div>
          ))}
      </Card>

      {/* Mobile View */}
      <Card className={`w-full ${screenData[id].borderColor} border-2 rounded-[10px]`} parentClassName={`relative md:hidden mb-10 mt-6 rounded-[15px] flex`}>
        <p className={`${screenData[id].borderColor} ${screenData[id].textColor} font-extrabold absolute right-0 -top-5 text-sm bg-white border-[3px] p-1 px-3 rounded-[4px]`}>{screenData[id].name}</p>

        {/* Render content for benefits cards on mobile */}
        {["waitingPeriodAndSubLimitsV2", "coverageAndLimits", "otherBenefitsAndLimits"].includes(id) &&
          Object.values(cardData || {})
            .filter((obj: any) => obj?.key)
            .map((obj: any, i: number) => {
              const isTableData = obj?.table_data;
              return obj?.key ? (
                <div key={i} className={`block w-full`}>
                  <div className={`flex gap-3 font-extrabold items-center w-full px-5 py-3 ${screenData[id].bgColor}`}>
                    <p className="w-4">
                      <TickIcon color={screenData[id].tickColor} />
                    </p>
                    <p dangerouslySetInnerHTML={{ __html: obj?.key }} />
                    {obj?.tooltip && <img src={tooltipIcon} className="w-4 h-4 min-w-4 min-h-4 object-contain flex-shrink-0 cursor-pointer" data-tooltip-id={obj.tooltip} data-tooltip-content={obj.tooltip} />}
                    <Tooltip delayShow={1} key={i} id={obj?.tooltip} place="bottom" className="max-w-xs w-auto whitespace-normal font-normal text-sm p-3 leading-relaxed bg-gray-800 text-white rounded-lg" render={() => <div className="text-white text-sm font-medium ">{obj?.tooltip}</div>} />
                  </div>

                  <div className={`w-full px-5 py-3 text-sm`}>
                    <p className="font-semibold text-gray-800 flex flex-wrap gap-1" dangerouslySetInnerHTML={{ __html: htmlHandler(obj?.value, screenData[id].bg) }} onClick={() => (isTableData ? setShowTableModal({ show: true, key: obj?.table_data }) : null)} />
                    {showTableModal.show && <TableModal tableData={showTableModal.key} onClose={() => setShowTableModal({ show: false, key: "" })} />}
                  </div>
                </div>
              ) : null;
            })}

        {/* Render content for exclusions on mobile */}
        {id === "exclusions" &&
          cardData &&
          cardData.map((data: any, i: number) => (
            <div key={i} className="flex w-full">
              <div className={`${screenData[id].bgColor} flex gap-3 font-extrabold items-center w-12 justify-center`}>
                <img src={cross} alt="Excluded" />
              </div>
              <div className="w-[92%] p-3">
                <p className="font-semibold text-gray-800" dangerouslySetInnerHTML={{ __html: data }}></p>
              </div>
            </div>
          ))}
      </Card>
    </>
  );
};

export default RecommendationbenefitCard;
